/**
 * Runtime polyfills for the browser floor in package.json "browserslist"
 * (iOS 13 / Safari 13.1 / Chrome 80 / Firefox 78 / Samsung Internet 12).
 *
 * Why this file exists (TAG-281): Next.js 16 compiles its own client runtime for
 * Safari 16.4+ and Chrome 111+. Lowering browserslist makes Turbopack emit
 * ES2019-safe syntax, but the framework still calls newer runtime APIs at
 * navigation time. Every install below is guarded, so current browsers are
 * untouched. Keep this list in step with the browserslist floor.
 */

type PolyfillTarget = typeof globalThis;

type Installed = Record<string, boolean>;

function define(obj: object, name: string, value: unknown): void {
  Object.defineProperty(obj, name, {
    value,
    writable: true,
    configurable: true,
    enumerable: false,
  });
}

function toIntegerOrInfinity(value: unknown): number {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  return Math.trunc(n);
}

function relativeIndex(length: number, index: unknown): number {
  const i = toIntegerOrInfinity(index);
  return i >= 0 ? i : length + i;
}

function arrayAt(this: ArrayLike<unknown>, index: unknown): unknown {
  const k = relativeIndex(this.length, index);
  return k < 0 || k >= this.length ? undefined : this[k];
}

function stringAt(this: string, index: unknown): string | undefined {
  const s = String(this);
  const k = relativeIndex(s.length, index);
  return k < 0 || k >= s.length ? undefined : s.charAt(k);
}

type Predicate = (value: unknown, index: number, array: ArrayLike<unknown>) => unknown;

function findLastIndex(this: ArrayLike<unknown>, predicate: Predicate, thisArg?: unknown): number {
  if (typeof predicate !== "function") {
    throw new TypeError("findLastIndex: predicate must be a function");
  }
  for (let i = this.length - 1; i >= 0; i -= 1) {
    if (predicate.call(thisArg, this[i], i, this)) return i;
  }
  return -1;
}

function findLast(this: ArrayLike<unknown>, predicate: Predicate, thisArg?: unknown): unknown {
  const i = findLastIndex.call(this, predicate, thisArg);
  return i === -1 ? undefined : this[i];
}

type Replacer = (substring: string, offset: number, whole: string) => string;

function replaceAll(this: string, search: string | RegExp, replacement: string | Replacer): string {
  const s = String(this);
  if (search instanceof RegExp) {
    if (!search.flags.includes("g")) {
      throw new TypeError("replaceAll must be called with a global RegExp");
    }
    return s.replace(search, replacement as string);
  }
  const needle = String(search);
  const replace: Replacer =
    typeof replacement === "function" ? replacement : () => String(replacement);
  if (needle === "") {
    // Spec behaviour: the replacement lands between every code unit.
    let out = "";
    for (let i = 0; i <= s.length; i += 1) {
      out += replace("", i, s);
      if (i < s.length) out += s.charAt(i);
    }
    return out;
  }
  const parts = s.split(needle);
  let out = parts[0];
  let pos = parts[0].length;
  for (let i = 1; i < parts.length; i += 1) {
    out += replace(needle, pos, s) + parts[i];
    pos += needle.length + parts[i].length;
  }
  return out;
}

class AggregateErrorPolyfill extends Error {
  errors: unknown[];

  constructor(errors: Iterable<unknown>, message?: string) {
    super(message);
    this.name = "AggregateError";
    this.errors = Array.from(errors);
  }
}

function promiseAny<T>(
  this: PromiseConstructor,
  iterable: Iterable<T | PromiseLike<T>>,
): Promise<T> {
  return new this((resolve, reject) => {
    const items = Array.from(iterable);
    if (items.length === 0) {
      reject(new AggregateErrorPolyfill([], "All promises were rejected"));
      return;
    }
    const errors: unknown[] = Array.from({ length: items.length });
    let pending = items.length;
    items.forEach((item, i) => {
      this.resolve(item).then(resolve, (err: unknown) => {
        errors[i] = err;
        pending -= 1;
        if (pending === 0) {
          reject(new AggregateErrorPolyfill(errors, "All promises were rejected"));
        }
      });
    });
  });
}

function hasOwn(obj: object, key: PropertyKey): boolean {
  if (obj === null || obj === undefined) {
    throw new TypeError("Object.hasOwn called on null or undefined");
  }
  return Object.prototype.hasOwnProperty.call(Object(obj), key);
}

function canParse(url: string, base?: string): boolean {
  try {
    return Boolean(new URL(url, base));
  } catch {
    return false;
  }
}

function dataCloneError(message: string): Error {
  if (typeof DOMException === "function") {
    try {
      return new DOMException(message, "DataCloneError");
    } catch {
      // Older WebKit cannot construct DOMException; fall through.
    }
  }
  const err = new Error(message);
  err.name = "DataCloneError";
  return err;
}

/**
 * Deep clone for the value shapes the app router hands to structuredClone:
 * plain objects, arrays, primitives, Date, RegExp, Map, Set, ArrayBuffer views.
 * Cycles are preserved. Functions and class instances throw like the native one.
 */
function structuredClonePolyfill<T>(value: T, seen = new Map<object, unknown>()): T {
  if (value === null || typeof value !== "object") {
    if (typeof value === "function" || typeof value === "symbol") {
      throw dataCloneError("The object could not be cloned.");
    }
    return value;
  }
  const obj = value as unknown as object;
  if (seen.has(obj)) return seen.get(obj) as T;

  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (obj instanceof RegExp) return new RegExp(obj.source, obj.flags) as unknown as T;
  if (typeof ArrayBuffer !== "undefined" && obj instanceof ArrayBuffer) {
    return obj.slice(0) as unknown as T;
  }
  if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(obj)) {
    const view = obj as ArrayBufferView;
    const Ctor = view.constructor as new (b: ArrayBuffer) => ArrayBufferView;
    const bytes = view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength);
    return new Ctor(bytes as ArrayBuffer) as unknown as T;
  }
  if (obj instanceof Map) {
    const out = new Map<unknown, unknown>();
    seen.set(obj, out);
    obj.forEach((v, k) =>
      out.set(structuredClonePolyfill(k, seen), structuredClonePolyfill(v, seen)),
    );
    return out as unknown as T;
  }
  if (obj instanceof Set) {
    const out = new Set<unknown>();
    seen.set(obj, out);
    obj.forEach((v) => out.add(structuredClonePolyfill(v, seen)));
    return out as unknown as T;
  }
  if (Array.isArray(obj)) {
    const out: unknown[] = Array.from({ length: obj.length });
    seen.set(obj, out);
    for (let i = 0; i < obj.length; i += 1) out[i] = structuredClonePolyfill(obj[i], seen);
    return out as unknown as T;
  }
  if (obj instanceof Error) {
    const out = new Error(obj.message);
    out.name = obj.name;
    if (obj.stack) out.stack = obj.stack;
    seen.set(obj, out);
    return out as unknown as T;
  }
  const proto = Object.getPrototypeOf(obj);
  if (proto !== Object.prototype && proto !== null) {
    throw dataCloneError("The object could not be cloned.");
  }
  const out: Record<string, unknown> = {};
  seen.set(obj, out);
  for (const key of Object.keys(obj)) {
    out[key] = structuredClonePolyfill((obj as Record<string, unknown>)[key], seen);
  }
  return out as unknown as T;
}

/**
 * Installs each missing API on `target`. Returns which names were installed so
 * tests (and a curious console) can see exactly what this browser lacked.
 */
export function installLegacyBrowserPolyfills(target: PolyfillTarget = globalThis): Installed {
  const installed: Installed = {};
  const g = target as unknown as Record<string, unknown>;
  const ObjectCtor = g.Object as typeof Object | undefined;
  const ArrayCtor = g.Array as typeof Array | undefined;
  const StringCtor = g.String as typeof String | undefined;
  const PromiseCtor = g.Promise as PromiseConstructor | undefined;
  const URLCtor = g.URL as typeof URL | undefined;

  if (ObjectCtor && typeof ObjectCtor.hasOwn !== "function") {
    define(ObjectCtor, "hasOwn", hasOwn);
    installed["Object.hasOwn"] = true;
  }
  if (ArrayCtor) {
    const proto = ArrayCtor.prototype as unknown as Record<string, unknown>;
    if (typeof proto.at !== "function") {
      define(ArrayCtor.prototype, "at", arrayAt);
      installed["Array.prototype.at"] = true;
    }
    if (typeof proto.findLast !== "function") {
      define(ArrayCtor.prototype, "findLast", findLast);
      installed["Array.prototype.findLast"] = true;
    }
    if (typeof proto.findLastIndex !== "function") {
      define(ArrayCtor.prototype, "findLastIndex", findLastIndex);
      installed["Array.prototype.findLastIndex"] = true;
    }
  }
  if (StringCtor) {
    const proto = StringCtor.prototype as unknown as Record<string, unknown>;
    if (typeof proto.at !== "function") {
      define(StringCtor.prototype, "at", stringAt);
      installed["String.prototype.at"] = true;
    }
    if (typeof proto.replaceAll !== "function") {
      define(StringCtor.prototype, "replaceAll", replaceAll);
      installed["String.prototype.replaceAll"] = true;
    }
  }
  if (typeof g.AggregateError !== "function") {
    define(target, "AggregateError", AggregateErrorPolyfill);
    installed.AggregateError = true;
  }
  if (
    PromiseCtor &&
    typeof (PromiseCtor as unknown as Record<string, unknown>).any !== "function"
  ) {
    define(PromiseCtor, "any", promiseAny);
    installed["Promise.any"] = true;
  }
  if (typeof g.structuredClone !== "function") {
    define(target, "structuredClone", (v: unknown) => structuredClonePolyfill(v));
    installed.structuredClone = true;
  }
  if (URLCtor && typeof (URLCtor as unknown as Record<string, unknown>).canParse !== "function") {
    define(URLCtor, "canParse", canParse);
    installed["URL.canParse"] = true;
  }
  return installed;
}

/* -------------------------------------------------------------------------- */
/* DOM polyfills (TAG-281 follow-up: iPhone 11 on iOS 13 showed the Next.js    */
/* "Application error" page; iPhone 12 on iOS 14 rendered raw HTML).           */
/* -------------------------------------------------------------------------- */

type ResizeCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => void;

interface WindowLike {
  addEventListener?: (type: string, fn: () => void) => void;
  removeEventListener?: (type: string, fn: () => void) => void;
  requestAnimationFrame?: (fn: () => void) => number;
  setTimeout?: (fn: () => void, ms: number) => number;
}

/**
 * ResizeObserver arrived in iOS 13.4. Embla (the carousels) and the Mux player
 * construct one without a guard, so iOS 13.0 to 13.3 threw a ReferenceError
 * inside a React effect and the app fell to the global error page. The shim
 * reports each observed element's box once on observe and again on every
 * window resize: enough for layout code that re-measures, without a real
 * observer. Current browsers keep their native implementation.
 */
function makeResizeObserverShim(win: WindowLike): typeof ResizeObserver {
  const schedule = (fn: () => void): void => {
    if (typeof win.requestAnimationFrame === "function") {
      win.requestAnimationFrame(fn);
    } else if (typeof win.setTimeout === "function") {
      win.setTimeout(fn, 0);
    } else {
      fn();
    }
  };

  class ResizeObserverShim {
    private readonly targets = new Set<Element>();
    private listening = false;
    private readonly callback: ResizeCallback;
    private readonly onResize = (): void => {
      this.deliver(Array.from(this.targets));
    };

    constructor(callback: ResizeCallback) {
      if (typeof callback !== "function") {
        throw new TypeError(
          "ResizeObserver: the callback provided as parameter 1 is not a function.",
        );
      }
      this.callback = callback;
    }

    observe(target: Element): void {
      if (!target || this.targets.has(target)) return;
      this.targets.add(target);
      if (!this.listening && typeof win.addEventListener === "function") {
        win.addEventListener("resize", this.onResize);
        this.listening = true;
      }
      schedule(() => {
        if (this.targets.has(target)) this.deliver([target]);
      });
    }

    unobserve(target: Element): void {
      this.targets.delete(target);
      if (this.targets.size === 0) this.stopListening();
    }

    disconnect(): void {
      this.targets.clear();
      this.stopListening();
    }

    private stopListening(): void {
      if (this.listening && typeof win.removeEventListener === "function") {
        win.removeEventListener("resize", this.onResize);
      }
      this.listening = false;
    }

    private deliver(targets: Element[]): void {
      if (targets.length === 0) return;
      const entries = targets.map((target) => {
        const rect = target.getBoundingClientRect();
        const size = [{ inlineSize: rect.width, blockSize: rect.height }];
        return {
          target,
          contentRect: rect,
          borderBoxSize: size,
          contentBoxSize: size,
          devicePixelContentBoxSize: size,
        } as unknown as ResizeObserverEntry;
      });
      this.callback(entries, this as unknown as ResizeObserver);
    }
  }

  return ResizeObserverShim as unknown as typeof ResizeObserver;
}

/** ParentNode.replaceChildren (Safari 14); the Mux player calls it directly. */
function replaceChildren(this: ParentNode, ...nodes: (Node | string)[]): void {
  while (this.firstChild) this.removeChild(this.firstChild);
  if (nodes.length > 0) this.append(...nodes);
}

/**
 * Installs the DOM-side polyfills on `win` (a Window). Separate from
 * `installLegacyBrowserPolyfills` because it needs a document; returns which
 * names were installed, like its sibling.
 */
export function installLegacyDomPolyfills(win: object): Installed {
  const installed: Installed = {};
  const g = win as Record<string, unknown>;
  if (typeof g.document === "undefined") return installed;

  if (typeof g.ResizeObserver !== "function") {
    define(win, "ResizeObserver", makeResizeObserverShim(win as WindowLike));
    installed.ResizeObserver = true;
  }

  for (const name of ["Element", "Document", "DocumentFragment"] as const) {
    const ctor = g[name] as { prototype?: Record<string, unknown> } | undefined;
    const proto = ctor?.prototype;
    if (proto && typeof proto.replaceChildren !== "function") {
      define(proto, "replaceChildren", replaceChildren);
      installed[`${name}.prototype.replaceChildren`] = true;
    }
  }
  return installed;
}

/* -------------------------------------------------------------------------- */
/* CSS cascade layers (TAG-281 follow-up: raw-HTML pages on iOS 13 and 14)     */
/* -------------------------------------------------------------------------- */

/**
 * Every MUI rule this app ships is wrapped in `@layer mui{ ... }` (CommonLayout
 * mounts AppRouterCacheProvider with enableCssLayer) so unlayered author CSS
 * from Contentful Studio wins without specificity fights. Safari added cascade
 * layers in 15.4. iOS 13, 14 and 15.0 to 15.3 treat `@layer` as an unknown
 * at-rule and drop the whole block: the server-rendered <style data-emotion>
 * tags lose every rule, and CSSStyleSheet.insertRule throws on each rule
 * emotion inserts at runtime (emotion swallows the error), so the page renders
 * as raw HTML. In those browsers we strip the wrapper and let the same rules
 * apply unlayered, at normal specificity. Browsers with cascade layers never
 * reach the fallback. `scripts/check-css-layers.mjs` fails the build if the
 * layer wrapper ships without this code.
 */
export const CSS_LAYER_FALLBACK_MARKER = "__adLegacyCssLayerFallback";

export function supportsCascadeLayers(win: object = globalThis): boolean {
  return typeof (win as Record<string, unknown>).CSSLayerBlockRule === "function";
}

/** Index of the `}` closing the block opened at `open`, or -1 if unterminated. */
const IDENT_CHAR = /[A-Za-z0-9_-]/;

/**
 * Walks `css` from `from`, skipping comments and quoted strings, and returns the
 * first index where `hit` matches; -1 when nothing matches or a comment/string
 * is left open (a streaming partial <style> text). The first cut scanned with a
 * bare indexOf and the skeptic pass caught it deleting real rules on the
 * ClearChoice globals.css, which mentions "@layer mui" inside doc comments and
 * ships inline (inlineCss). Both brands inline globals.css.
 */
function scanCode(css: string, from: number, hit: (i: number) => boolean): number {
  let quote: string | null = null;
  for (let i = from; i < css.length; i++) {
    const ch = css[i];
    if (quote) {
      if (ch === "\\") i++;
      else if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === "/" && css[i + 1] === "*") {
      const end = css.indexOf("*/", i + 2);
      if (end === -1) return -1;
      i = end + 1;
      continue;
    }
    if (hit(i)) return i;
  }
  return -1;
}

/** Index of the next `@layer` at-rule token outside comments and strings. */
function nextLayerRule(css: string, from: number): number {
  return scanCode(
    css,
    from,
    (i) => css[i] === "@" && css.startsWith("@layer", i) && !IDENT_CHAR.test(css[i + 6] ?? ""),
  );
}

/** Index of the `}` closing the block opened at `open`, or -1 if unterminated. */
function matchingBrace(css: string, open: number): number {
  let depth = 0;
  return scanCode(css, open, (i) => {
    if (css[i] === "{") depth++;
    else if (css[i] === "}") depth--;
    return depth === 0 && css[i] === "}";
  });
}

/**
 * Strips every `@layer <names>{ ... }` wrapper (keeping its body) and drops bare
 * `@layer a, b;` statements. An unterminated block is left untouched: the
 * streaming HTML parser can hand us a partial <style> text, and the next
 * mutation completes it.
 */
export function unwrapCascadeLayers(css: string): string {
  let out = css;
  let from = 0;
  for (;;) {
    const at = nextLayerRule(out, from);
    if (at === -1) return out;
    let i = at + "@layer".length;
    while (i < out.length && out[i] !== "{" && out[i] !== ";") i++;
    if (i >= out.length) return out;
    if (out[i] === ";") {
      out = out.slice(0, at) + out.slice(i + 1);
      from = at;
      continue;
    }
    const close = matchingBrace(out, i);
    if (close === -1) return out;
    out = out.slice(0, at) + out.slice(i + 1, close) + out.slice(close + 1);
    from = at;
  }
}

/** Splits a stylesheet text into its top-level rules (insertRule takes one). */
export function splitTopLevelRules(css: string): string[] {
  const rules: string[] = [];
  let pos = 0;
  while (pos < css.length) {
    const open = scanCode(css, pos, (i) => css[i] === "{");
    if (open === -1) break;
    const close = matchingBrace(css, open);
    if (close === -1) break;
    const rule = css.slice(pos, close + 1).trim();
    if (rule) rules.push(rule);
    pos = close + 1;
  }
  return rules;
}

interface StyleSheetLike {
  insertRule?: (rule: string, index?: number) => number;
}

/**
 * Applies the fallback to `doc`/`win` when cascade layers are unsupported:
 * rewrites the <style> tags already present, watches for the ones the
 * streaming parser and React add later, and unwraps the rules emotion inserts
 * through CSSStyleSheet.insertRule. Returns true when the fallback is active.
 */
export function installCascadeLayerFallback(
  doc: Document | null = typeof document === "undefined" ? null : document,
  win: object = globalThis,
): boolean {
  if (!doc || supportsCascadeLayers(win)) return false;
  const g = win as Record<string, unknown>;
  if (g[CSS_LAYER_FALLBACK_MARKER]) return true;
  define(win, CSS_LAYER_FALLBACK_MARKER, true);

  const unwrapStyle = (el: Element): void => {
    const text = el.textContent ?? "";
    if (text.indexOf("@layer") === -1) return;
    const next = unwrapCascadeLayers(text);
    if (next !== text) el.textContent = next;
  };

  for (const el of Array.from(doc.querySelectorAll("style"))) unwrapStyle(el);

  const MO = g.MutationObserver as typeof MutationObserver | undefined;
  if (typeof MO === "function" && doc.documentElement) {
    const isStyle = (n: Node | null): n is Element => !!n && (n as Element).tagName === "STYLE";
    const observer = new MO((records) => {
      for (const record of records) {
        if (record.type === "characterData") {
          if (isStyle(record.target.parentNode)) {
            unwrapStyle(record.target.parentNode);
          }
          continue;
        }
        record.addedNodes.forEach((node) => {
          if (isStyle(node)) unwrapStyle(node);
          else if (isStyle(node.parentNode)) unwrapStyle(node.parentNode);
        });
      }
    });
    observer.observe(doc.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  const Sheet = g.CSSStyleSheet as { prototype?: StyleSheetLike } | undefined;
  const proto = Sheet?.prototype;
  if (proto && typeof proto.insertRule === "function") {
    const original = proto.insertRule;
    define(
      proto,
      "insertRule",
      function insertRule(this: CSSStyleSheet, rule: string, index?: number): number {
        if (typeof rule !== "string" || rule.indexOf("@layer") === -1) {
          return original.call(this, rule, index);
        }
        const rules = splitTopLevelRules(unwrapCascadeLayers(rule));
        if (rules.length === 0) return index ?? 0;
        let at = index ?? 0;
        let last = at;
        for (const r of rules) {
          last = original.call(this, r, at);
          at = last + 1;
        }
        return last;
      },
    );
  }
  return true;
}
